import { useCallback, useEffect, useRef, useState } from 'react';
import { getCustomMetricScanJob, scanCustomMetricSuggestions } from '../api';

const ACTIVE_STATUSES = new Set(['queued', 'running']);

export default function useCustomMetricScan({ ticker, getToken, onComplete }) {
    const [state, setState] = useState({ ticker: '', job: null, submitting: false, error: null });
    const [submitError, setSubmitError] = useState(null);
    const [revision, setRevision] = useState(0);
    const generation = useRef(0);
    const completedJob = useRef(null);
    const primedTicker = useRef(null);

    useEffect(() => {
        const requestGeneration = ++generation.current;
        let timer;
        const current = () => generation.current === requestGeneration;
        if (!ticker) {
            primedTicker.current = null;
            completedJob.current = null;
            return;
        }

        const poll = async () => {
            try {
                const token = await getToken();
                if (!token || !current()) return;
                const { job } = await getCustomMetricScanJob(ticker, token);
                if (!current()) return;
                setState({ ticker, job, submitting: false, error: null });
                // An older terminal row is not proof this POST created or joined a job.
                if (ACTIVE_STATUSES.has(job?.status)) setSubmitError(null);
                const alreadyWatching = primedTicker.current === ticker;
                if (job?.status === 'complete' && completedJob.current !== job.id) {
                    completedJob.current = job.id;
                    if (alreadyWatching) onComplete(ticker);
                }
                primedTicker.current = ticker;
                if (ACTIVE_STATUSES.has(job?.status)) {
                    timer = window.setTimeout(poll, 2500);
                }
            } catch (err) {
                if (!current()) return;
                // Failed polls still count as watching so a later `complete` is not treated as restore.
                primedTicker.current = ticker;
                setState((previous) => ({
                    ...(previous.ticker === ticker ? previous : { job: null }),
                    ticker,
                    submitting: false,
                    error: 'Unable to check scan progress. Reconnecting…',
                }));
                // A network failure must not cancel or restart the durable job.
                timer = window.setTimeout(poll, 5000);
            }
        };
        void poll();
        return () => {
            generation.current += 1;
            window.clearTimeout(timer);
        };
    }, [ticker, getToken, onComplete, revision]);

    const startScan = useCallback(async (limit) => {
        const requestGeneration = ++generation.current;
        setSubmitError(null);
        setState({ ticker, job: null, submitting: true, error: null });
        try {
            const token = await getToken();
            if (!token || generation.current !== requestGeneration) return;
            const { job } = await scanCustomMetricSuggestions({ ticker, limit }, token);
            if (generation.current !== requestGeneration) return;
            // An accepted submission is being watched even if the initial GET was cancelled.
            // Its first completed status must refresh the queue, not count as a restore.
            primedTicker.current = ticker;
            setState({ ticker, job, submitting: false, error: null });
        } catch (err) {
            if (generation.current !== requestGeneration) return;
            setSubmitError({ ticker, message: err?.message || 'Unable to queue the scan.' });
            setState({ ticker, job: null, submitting: false, error: null });
        } finally {
            // Also reconcile uncertain POST outcomes rather than blindly re-posting.
            if (generation.current === requestGeneration) setRevision((value) => value + 1);
        }
    }, [ticker, getToken]);

    const visible = state.ticker === ticker ? state : { job: null, submitting: false, error: null };
    return {
        ...visible,
        error: visible.error || (submitError?.ticker === ticker ? submitError.message : null),
        scanning: visible.submitting || ACTIVE_STATUSES.has(visible.job?.status),
        startScan,
    };
}
